import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://www.utrechtairporttaxi.nl/og-image.jpg'
}: SEOProps) => {
  useEffect(() => {
    // Set title
    document.title = title;

    // Set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }

    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:image', ogImage, true);
    
    if (canonical) {
      setMetaTag('og:url', canonical, true);
    }

    // Twitter tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

    // Canonical link
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      
      linkElement.setAttribute('href', canonical);
    }
  }, [title, description, keywords, canonical, ogImage]);
};

// Generate sitemap XML content
export const generateSitemap = (): string => {
  const baseUrl = 'https://www.utrechtairporttaxi.nl';
  const today = new Date().toISOString().split('T')[0];
  
  const urls = [
    { loc: '/', priority: '1.0', changefreq: 'daily' },
    { loc: '/prijs-berekenen', priority: '0.9', changefreq: 'weekly' },
    { loc: '/werkgebied', priority: '0.8', changefreq: 'monthly' },
    { loc: '/taxi-utrecht-schiphol', priority: '0.9', changefreq: 'weekly' },
    { loc: '/taxi-utrecht-eindhoven-airport', priority: '0.8', changefreq: 'weekly' },
    { loc: '/taxi-utrecht-rotterdam-airport', priority: '0.8', changefreq: 'weekly' },
    { loc: '/taxi-amersfoort-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-amersfoort-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-amersfoort-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-breukelen-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-breukelen-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-breukelen-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-bunnik-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-bunnik-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-bunnik-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-de-bilt-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-de-bilt-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-de-bilt-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-houten-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-houten-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-houten-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-ijsselstein-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-ijsselstein-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-ijsselstein-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-maarssen-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-maarssen-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-maarssen-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-nieuwegein-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-nieuwegein-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-nieuwegein-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-soest-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-soest-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-soest-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-vianen-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-vianen-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-vianen-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-woerden-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-woerden-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-woerden-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-zeist-schiphol', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-zeist-eindhoven-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/taxi-zeist-rotterdam-airport', priority: '0.7', changefreq: 'monthly' },
    { loc: '/privacy', priority: '0.3', changefreq: 'yearly' },
    { loc: '/algemene-voorwaarden', priority: '0.3', changefreq: 'yearly' },
    { loc: '/cookies', priority: '0.3', changefreq: 'yearly' },
  ];

  const urlEntries = urls.map(({ loc, priority, changefreq }) => `  <url>
    <loc>${baseUrl}${loc}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${changefreq}</changefreq>
    <priority>${priority}</priority>
  </url>`).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlEntries}
</urlset>`;
};

// Generate robots.txt content
export const generateRobotsTxt = (): string => {
  const baseUrl = 'https://www.utrechtairporttaxi.nl';
  
  return `User-agent: *
Allow: /

Sitemap: ${baseUrl}/sitemap.xml`;
};
